import type { PlainMessage } from '@bufbuild/protobuf';
import { createState } from 'lexical';

import type { NoteLine } from '@/shared/api/protocol-ts/model/dto_scribe_catalog_pb';

import type { DocumentSectionSpec, NoteEditorBlockDef } from '../../../config';

export type TemplateLineDef = PlainMessage<NoteLine>;

// A text node knows the text block it was read as and the line it was read
// in: text that moves to another line becomes a block of that line.
export type TemplateWording = { def: NoteEditorBlockDef; lineId: string };

const parseAs =
  <V>(fallback: V) =>
  (value: unknown) =>
    (value ?? fallback) as V;

// What a template keeps that its text does not show rides on the node as
// read, its keys in order, so an unchanged template is written back as it
// was read. A section's lines are the paragraphs after its heading; a
// line's blocks are read from its paragraph, but for empty text blocks,
// which no text node holds and which keep their place among the blocks as
// read.
export const templateSectionState = createState('templateSection', {
  parse: parseAs<DocumentSectionSpec | null>(null),
});

export const templateLineState = createState('templateLine', {
  parse: parseAs<TemplateLineDef | null>(null),
});

export const templateWordingState = createState('templateWording', {
  parse: parseAs<TemplateWording | null>(null),
});

// What an AI instruction is: a slot the AI writes text into, or the
// guidance of its whole section, which is never printed and stands alone
// on the first line under the section's heading.
export type TemplateInstructionRole = 'slot' | 'section';

export const templateInstructionRoleState = createState(
  'templateInstructionRole',
  {
    parse: (value): TemplateInstructionRole =>
      value === 'section' ? 'section' : 'slot',
  },
);
